import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MdOutlineSettings, MdOutlineStar, MdOutlineStarBorder, MdHistory, MdSearch } from "react-icons/md";
import { TbLayoutSidebarLeftCollapse } from "react-icons/tb";
import useNavModules from "./useNavModules";
import { SETTINGS_MODULE_ID, itemForPath, needsExactMatch } from "./navModules";
import { openNavSearch, pushRecent } from "./navPrefs";
import CommandPalette from "./CommandPalette";
import "./navV2.css";

export const RAIL_W = 76;
export const PANEL_W = 232;
export const SETTINGS_PATH = "/settings";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

/**
 * New desktop/tablet menu: a dark module rail plus a page panel for the chosen
 * module. Content comes from getNavModules() (a regrouping of getNavGroups), so
 * a person sees exactly the pages the old sidebar would have shown them.
 *
 * `overlay` (tablets): the panel floats over the page with a scrim and folds
 * away after a pick, so the content keeps the full width minus the rail.
 */
export default function SidebarV2({ vendorData, overlay = false, onWidthChange }) {
  const { modules, settings, items, byKey, approvalsPending, prefs } = useNavModules(vendorData);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const current = useMemo(() => itemForPath(pathname, items), [pathname, items]);
  const routeModuleId = pathname === SETTINGS_PATH ? SETTINGS_MODULE_ID : current?.moduleId || null;

  const [selected, setSelected] = useState(routeModuleId || "home");
  const [overlayOpen, setOverlayOpen] = useState(false);

  // The panel follows the page you're on - except for one-page modules, which
  // have nothing to list, so the panel you had stays put.
  useEffect(() => {
    if (!routeModuleId) return;
    const mod = modules.find((m) => m.id === routeModuleId);
    if (routeModuleId === SETTINGS_MODULE_ID || (mod && !mod.single)) setSelected(routeModuleId);
  }, [routeModuleId, modules]);

  useEffect(() => {
    if (current) pushRecent(current.key);
  }, [current]);

  // Tablet: picking a page (or rotating) folds the floating panel away.
  useEffect(() => {
    setOverlayOpen(false);
  }, [pathname, overlay]);

  const settingsModule = { id: SETTINGS_MODULE_ID, label: "Settings", sections: [{ title: null, items: settings }], items: settings };
  const panelModule = selected === SETTINGS_MODULE_ID ? settingsModule : modules.find((m) => m.id === selected) || null;
  const panelShown = !!panelModule && (overlay ? overlayOpen : prefs.panelOpen);

  useEffect(() => {
    onWidthChange?.(RAIL_W + (panelShown && !overlay ? PANEL_W : 0));
  }, [panelShown, overlay, onWidthChange]);

  const openPanel = (id) => {
    setSelected(id);
    if (overlay) setOverlayOpen(true);
    else if (!prefs.panelOpen) prefs.setPanelOpen(true);
  };
  const closePanel = () => (overlay ? setOverlayOpen(false) : prefs.setPanelOpen(false));

  const onRail = (mod) => {
    if (mod.single) {
      navigate(mod.items[0].to);
      return;
    }
    if (selected === mod.id && panelShown) closePanel();
    else openPanel(mod.id);
  };

  const onSettings = () => {
    if (pathname !== SETTINGS_PATH) navigate(SETTINGS_PATH);
    openPanel(SETTINGS_MODULE_ID);
  };

  const pinnedItems = prefs.pins.map((k) => byKey.get(k)).filter(Boolean);
  const recentItems = prefs.recent
    .map((k) => byKey.get(k))
    .filter((i) => i && !prefs.pins.includes(i.key))
    .slice(0, 5);

  const renderRow = (item, Icon = item.icon) => {
    const pinned = prefs.pins.includes(item.key);
    const active = current?.key === item.key;
    const count = item.key === "approvals" && approvalsPending > 0 ? approvalsPending : 0;
    return (
      <div key={item.key} className={`nv2-row${active ? " is-active" : ""}`}>
        <NavLink to={item.to} end={needsExactMatch(item, items)} className="nv2-link" aria-current={active ? "page" : undefined}>
          {Icon ? <Icon size={19} /> : null}
          <span className="nv2-text">{item.label}</span>
          {count ? <span className="nv2-count">{count}</span> : null}
        </NavLink>
        <button
          type="button"
          className={`nv2-star${pinned ? " is-pinned" : ""}`}
          onClick={() => prefs.togglePin(item.key)}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
          title={pinned ? "Unpin" : "Pin to Home"}
        >
          {pinned ? <MdOutlineStar size={16} /> : <MdOutlineStarBorder size={16} />}
        </button>
      </div>
    );
  };

  const railButton = (mod, { badge = 0, onClick, Icon = mod.icon } = {}) => {
    const isCurrent = routeModuleId === mod.id;
    const isOpen = panelShown && panelModule?.id === mod.id;
    return (
      <button
        key={mod.id}
        type="button"
        className={`nv2-rail-btn${isCurrent ? " is-current" : ""}${isOpen && !isCurrent ? " is-open" : ""}`}
        onClick={onClick || (() => onRail(mod))}
        title={mod.label}
        aria-label={badge ? `${mod.label}, ${badge} waiting` : mod.label}
        aria-expanded={mod.single ? undefined : isOpen}
        aria-current={isCurrent ? "page" : undefined}
      >
        <Icon size={22} />
        <span className="nv2-rail-label">{mod.label}</span>
        {badge ? <span className="nv2-rail-badge">{badge > 99 ? "99+" : badge}</span> : null}
      </button>
    );
  };

  return (
    <>
      {overlay &&
        panelShown &&
        ReactDOM.createPortal(<div className="sidebar-scrim" onClick={() => setOverlayOpen(false)} />, document.body)}

      <nav className="nv2" data-overlay={overlay ? "true" : "false"} aria-label="Main menu">
        <div className="nv2-rail">
          <div className="nv2-rail-list">
            {modules.map((mod) => railButton(mod, { badge: mod.id === "home" ? approvalsPending : 0 }))}
          </div>
          <div className="nv2-rail-foot">
            {railButton(settingsModule, { onClick: onSettings, Icon: MdOutlineSettings })}
          </div>
        </div>

        {panelShown && (
          <div className="nv2-panel">
            <div className="nv2-panel-head">
              <h2>{panelModule.label}</h2>
              <button type="button" className="nv2-icon-btn" onClick={closePanel} aria-label="Hide page panel" title="Hide panel">
                <TbLayoutSidebarLeftCollapse size={17} />
              </button>
            </div>

            <div className="nv2-panel-body">
              {panelModule.sections.map((sec, i) => (
                <div key={sec.title || `s${i}`}>
                  {sec.title ? <div className="nv2-sec">{sec.title}</div> : null}
                  {sec.items.map((item) => renderRow(item))}
                </div>
              ))}
              {panelModule.id === SETTINGS_MODULE_ID && settings.length === 0 && (
                <div className="nv2-hint">No setup pages for your role.</div>
              )}

              {panelModule.id === "home" && (
                <>
                  <div className="nv2-sec">Pinned</div>
                  {pinnedItems.length ? (
                    pinnedItems.map((item) => renderRow(item))
                  ) : (
                    <div className="nv2-hint">Hover any page and click ☆ to pin it here.</div>
                  )}
                  {recentItems.length > 0 && (
                    <>
                      <div className="nv2-sec">Recent</div>
                      {recentItems.map((item) => renderRow(item, MdHistory))}
                    </>
                  )}
                </>
              )}
            </div>

            <div className="nv2-panel-foot">
              <button type="button" className="nv2-search-btn" onClick={openNavSearch}>
                <MdSearch size={16} /> Search
                <span className="nv2-kbd">{isMac ? "⌘K" : "Ctrl K"}</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      <CommandPalette items={items} byKey={byKey} pins={prefs.pins} recent={prefs.recent} />
    </>
  );
}
